import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TBusinessType } from '../../entities/general.entities';
import { selectAccount } from '../../store/auth/auth.selector';
import { TLang } from '../../entities/ui.entities';
import {
  IFarmResource,
  ILineResource,
  IMusselLineResource,
  IOysterLineResource,
} from '../../entities/farms.entities';
import { translate, transUnit } from '../../lib/lang.helper';
import { Modal } from 'antd';
import { Button, CheckboxButton, Subtitle } from '../shared';
import { selectLang } from '../../store/ui/ui.selector';
import { updateAccountSetting } from '../../store/auth/auth.actions';

const MusselHeaders = [
    'current_line_length',
    'backbone',
    'planned_date_seed',
    'shell_size',
    'planned_date_harvest',
    'spat_seed_type',
    'stage',
    'beams_count',
    'submersion',
    'density',
    'inventories',
    'predicted_amount',
    'predicted_date',
    'line_fill_percent',
    'spacing',
    'drop',
  ],
  OysterHeaders = [
    'current_basket_count',
    'backbone',
    'planned_date_seed',
    'shell_size',
    'planned_date_harvest',
    'spat_seed_type',
    'stage',
    'inventories',
    'predicted_amount',
    'predicted_date',
    'line_fill_percent',
    'spacing',
  ];

const labels = {
  planned_date_seed: 'Date seeded',
  shell_size: 'Avg. Size',
  planned_date_harvest: 'Planned Harvest Date',
  spat_seed_type: 'Seed type',
  stage: 'Stage',
  density: 'Density',
  beams_count: 'Number of beams',
  submersion: 'Submersion',
  inventories: 'Inventories',
  predicted_date: 'Predicted Harvest Date',
  line_fill_percent: 'Line Fill Percent',
  spacing: 'Dropper Spacing',
  drop: 'Depth',
};

export const getMusselHeaderLabel = (
  key: string,
  lang: TLang | undefined,
  line: IMusselLineResource | undefined = undefined,
  farm: IFarmResource | undefined = undefined,
) => {
  if (key === 'current_line_length') {
    return translate(
      lang,
      line?.line_type === 'LONGLINE' ? 'Longline Length' : 'Number of droppers',
    );
  }
  if (key === 'backbone') {
    return transUnit(lang, 'backbone', farm?.structure);
  }
  if (key === 'predicted_amount') {
    return translate(lang, '_predicted_harvest_tons');
  }
  if (Object.keys(labels).includes(key)) {
    return translate(lang, labels[key as keyof typeof labels]);
  }
  return key;
};

export const getOysterHeaderLabel = (
  key: string,
  lang: TLang | undefined,
  line: IOysterLineResource | undefined = undefined,
) => {
  if (key === 'current_basket_count') {
    return translate(lang, 'Number of %s', line?.farming_method);
  }
  if (key === 'backbone') {
    return translate(lang, 'Line Length');
  }
  if (key === 'predicted_amount') {
    return translate(lang, '_predicted_harvest_dozens');
  }
  if (Object.keys(labels).includes(key)) {
    return translate(lang, labels[key as keyof typeof labels]);
  }
  return key;
};

interface Props {
  type: TBusinessType;
  visible: boolean;
  line?: ILineResource;
  farm?: IFarmResource;
  onClose: () => void;
}

const LineHeadersModal = ({
  type: cropType,
  visible,
  line,
  farm,
  onClose,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const accountSettings = useSelector(selectAccount);

  const headerItems =
    cropType === 'MUSSEL'
      ? accountSettings?.mussel_line_headers
      : accountSettings?.oyster_line_headers;
  const allItems = cropType === 'MUSSEL' ? MusselHeaders : OysterHeaders;

  const [disabled, setDisabled] = useState(false);
  const [selectedItems, setSelectedItems] = useState(headerItems ?? []);

  const isSelected = (item: string) => selectedItems.includes(item);
  const toggleSelect = (item: string) => {
    if (isSelected(item)) {
      setSelectedItems(selectedItems.filter(i => i !== item));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const confirmClick = async () => {
    setDisabled(true);
    const res = await dispatch(
      updateAccountSetting({
        ...(cropType === 'MUSSEL'
          ? { mussel_line_headers: selectedItems }
          : { oyster_line_headers: selectedItems }),
      }),
    );
    if (res !== true) {
      setDisabled(false);
      window.alert(res ?? 'Error');
    } else {
      onClose();
    }
  };

  return (
    <Modal visible={visible} onCancel={onClose} footer={null}>
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, `Update ${cropType.toLowerCase()} line headers`)}
          </Subtitle>
        </div>
        <div className='section-modal'>
          <div className='section'>
            {allItems.map(x => (
              <div key={x} className='item-row mb-12 ml-24'>
                <CheckboxButton
                  label={
                    cropType === 'MUSSEL'
                      ? getMusselHeaderLabel(
                          x,
                          lang,
                          line as IMusselLineResource,
                          farm,
                        )
                      : getOysterHeaderLabel(
                          x,
                          lang,
                          line as IOysterLineResource,
                        )
                  }
                  checked={isSelected(x)}
                  onChange={() => toggleSelect(x)}
                />
              </div>
            ))}
          </div>
        </div>
        <div className='modal-button d-flex justify-end align-center'>
          <Button
            width={'small'}
            size={2}
            type='fill'
            color='green'
            onClick={confirmClick}
            disabled={disabled}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default LineHeadersModal;
